import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import {
  googleReviews,
  googleReviewSummary,
  type GoogleReview,
} from 'src/data/google-reviews';

// How many review cards to show at once (a clean 2 x 3 grid).
const DISPLAY_COUNT = 6;

// Fisher-Yates shuffle, then take the first n. Called once per page load
// so every refresh surfaces a different mix of reviews.
const pickRandom = <T,>(arr: T[], n: number): T[] => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
};

// =====================================================================
//  Google Reviews section (homepage, sits between the FAQ and the
//  footer). Renders real Google reviews word-for-word with the
//  reviewer's photo, star rating, and a "View all Google reviews"
//  link. Content is sourced from src/data/google-reviews.ts.
// =====================================================================

// ---- Reveal-on-scroll wrapper -----------------------------------------
// threshold 0 so it always fires, even when the wrapped block is taller
// than the viewport on mobile; latches visible so it never hides again.
const Reveal: FC<{ children: ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter',
      }}
    >
      {children}
    </Box>
  );
};

// ---- Four-colour Google "G" -------------------------------------------
const GoogleG: FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 48 48" aria-hidden sx={{ display: 'block', flexShrink: 0 }}>
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </Box>
);

// ---- Single gold star --------------------------------------------------
const Star: FC<{ size?: number; filled?: boolean }> = ({ size = 16, filled = true }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden
    sx={{ display: 'block', flexShrink: 0 }}
  >
    <path
      fill={filled ? '#FBBC05' : '#e4e4e7'}
      d="M12 17.27l5.18 3.13-1.37-5.9 4.58-3.97-6.03-.52L12 4.5 9.64 10.08l-6.03.52 4.58 3.97-1.37 5.9z"
    />
  </Box>
);

const StarRow: FC<{ rating: number; size?: number }> = ({ rating, size = 16 }) => (
  <Stack direction="row" spacing={0.25} aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} filled={n <= Math.round(rating)} />
    ))}
  </Stack>
);

// ---- Arrow ------------------------------------------------------------
// Inline SVG with a proper viewBox so it scales cleanly at any size.
// (The icon-library arrow has no viewBox and clips when shrunk.)
const ArrowRight: FC<{ size?: number }> = ({ size = 14 }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    sx={{ display: 'block', flexShrink: 0 }}
  >
    <path
      d="M5 12h14m0 0-6-6m6 6-6 6"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

// ---- Reviewer avatar (photo, or initials fallback) --------------------
const Avatar: FC<{ name: string; photo?: string }> = ({ name, photo }) => {
  const [errored, setErrored] = useState(false);
  const showPhoto = photo && !errored;

  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        flexShrink: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
      }}
    >
      {showPhoto ? (
        <Box
          component="img"
          src={photo}
          alt={name}
          referrerPolicy="no-referrer"
          onError={() => setErrored(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 15,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '0.01em',
          }}
        >
          {getInitials(name)}
        </Typography>
      )}
    </Box>
  );
};

// ---- One review card ---------------------------------------------------
// Review text is clamped to this many lines so every card is the same
// height. Longer reviews get a "See more" link to the full Google review.
const CLAMP_LINES = 6;

const ReviewCard: FC<{ review: GoogleReview }> = ({ review }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  // Only show "See more" on cards whose text is actually being cut off.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setIsClamped(el.scrollHeight > el.clientHeight + 2);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        p: { xs: 3, md: 3.5 },
        height: '100%',
        background: '#ffffff',
        border: '1px solid #e4e4e7',
        borderRadius: 3,
        transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease, border-color 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 40px -16px rgba(15, 23, 42, 0.18)',
          borderColor: '#d4d4d8',
        },
      }}
    >
      {/* Google G, top-right */}
      <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
        <GoogleG size={20} />
      </Box>

      {/* Reviewer */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar name={review.name} photo={review.photo} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 15,
              fontWeight: 600,
              color: '#0a0a0b',
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
            }}
          >
            {review.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 12.5,
              fontWeight: 400,
              color: '#a1a1aa',
            }}
          >
            {review.date}
          </Typography>
        </Box>
      </Stack>

      <StarRow rating={review.rating} size={16} />

      {/* Review text, clamped so every card stays the same height */}
      <Typography
        ref={textRef}
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: 14.5,
          fontWeight: 400,
          lineHeight: 1.65,
          color: '#52525b',
          display: '-webkit-box',
          WebkitLineClamp: CLAMP_LINES,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {review.text}
      </Typography>

      {/* "See more" — opens the full review on Google */}
      {isClamped && (
        <Box
          component="a"
          href={googleReviewSummary.viewAllUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 0.5,
            fontFamily: '"Inter", sans-serif',
            fontSize: 13.5,
            fontWeight: 600,
            color: '#2563eb',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            '&:hover .see-more-arrow': { transform: 'translateX(3px)' },
          }}
        >
          See more
          <Box
            component="span"
            className="see-more-arrow"
            sx={{ display: 'inline-flex', transition: 'transform 0.2s ease' }}
          >
            <ArrowRight size={14} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

// =====================================================================
//  Section
// =====================================================================
export const HomeGoogleReviews: FC = () => {
  const { rating, totalReviews, viewAllUrl } = googleReviewSummary;

  // Picked once per mount — a page refresh remounts and reshuffles,
  // so visitors see a different set of reviews each time.
  const displayed = useMemo(() => pickRandom(googleReviews, DISPLAY_COUNT), []);

  return (
    <Box
      id="reviews"
      sx={{
        py: { xs: 12, md: 18 },
        borderTop: '1px solid #f4f4f5',
        background: '#fafafa',
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Reveal>
          <Stack spacing={3} alignItems="center" sx={{ mb: { xs: 6, md: 9 } }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{ width: 24, height: '1px', background: '#0a0a0b' }} />
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#0a0a0b',
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                }}
              >
                Client Reviews
              </Typography>
            </Stack>

            <Typography
              align="center"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '36px', md: '60px' },
                fontWeight: 700,
                lineHeight: 1.04,
                letterSpacing: '-0.035em',
                color: '#0a0a0b',
              }}
            >
              What our clients{' '}
              <Box component="span" sx={{ color: '#2563eb' }}>say on Google</Box>
            </Typography>

            {/* Aggregate rating pill */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 2.5,
                py: 1.25,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                borderRadius: 999,
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)',
              }}
            >
              <GoogleG size={22} />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 17,
                  fontWeight: 700,
                  color: '#0a0a0b',
                }}
              >
                {rating.toFixed(1)}
              </Typography>
              <StarRow rating={rating} size={17} />
              <Box sx={{ width: '1px', height: 18, background: '#e4e4e7' }} />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: '#52525b',
                }}
              >
                {totalReviews} Google reviews
              </Typography>
            </Stack>
          </Stack>
        </Reveal>

        {/* Review grid */}
        <Reveal delay={0.05}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: { xs: 2.5, md: 3 },
            }}
          >
            {displayed.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </Box>
        </Reveal>

        {/* View all Google reviews */}
        <Reveal delay={0.1}>
          <Stack alignItems="center" sx={{ mt: { xs: 6, md: 8 } }}>
            <Box
              component="a"
              href={viewAllUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 1.75,
                background: '#0a0a0b',
                borderRadius: 999,
                textDecoration: 'none',
                fontFamily: '"Inter", sans-serif',
                fontSize: 15,
                fontWeight: 600,
                color: '#ffffff',
                letterSpacing: '-0.01em',
                transition: 'transform 0.2s ease, background 0.2s ease',
                '&:hover': {
                  background: '#2563eb',
                  transform: 'translateY(-2px)',
                  '& .review-arrow': { transform: 'translateX(4px)' },
                },
              }}
            >
              View all Google reviews
              <Box
                component="span"
                className="review-arrow"
                sx={{
                  display: 'inline-flex',
                  transition: 'transform 0.2s ease',
                }}
              >
                <ArrowRight size={18} />
              </Box>
            </Box>
          </Stack>
        </Reveal>
      </Container>
    </Box>
  );
};
